import { useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import useDirectory from 'hooks/useDirectory'
import { Building } from 'models/Building'
import { useRecoilState, useRecoilValue } from 'recoil'
import { listPointState } from 'store/buildingStore'
import { currentAnnotation } from 'store/krpanoStore'
import currentViewerState from 'store/viewerStore'
import useSWR from 'swr'
import { FloorMapElement, Point } from 'types/building'

import Spinner from 'components/atoms/Spinner'

import AnnotationModal from '../buildingDetailViewMode/components/AnnotationModal/AnnotationModal'

import BusinessInfoModal from './components/BusinessInfoModal'
import MapLocationModal from './components/map/MapLocationModal'
import TourViewerFloorMap from './components/map/TourViewerFloorMap'
import ThumbnailCarousel from './components/ThumbnailCarousel/ThumbnailCarousel'
import TourViewerPanorama from './components/TourViewerPanorama'

export default function TourViewPage() {
  const prams = useParams()
  const { getBuiildingType, directory } = useDirectory()
  const [currentViewer, setCurrentViewer] = useRecoilState(currentViewerState)
  const [searchParams] = useSearchParams()
  const floorId = searchParams.get('floor') || undefined
  const areaId = searchParams.get('area')
  const pointId = searchParams.get('point')
  const subimageId = searchParams.get('subimage')
  const [selectedPointID, setSelectedPointID] = useState<string>('')
  const [points, setPoints] = useRecoilState(listPointState)
  const [openBusinessInfo, setOpenBusinessInfo] = useState(false)
  const [openMapLocation, setOpenMapLocation] = useState(false)
  const currentSelected = useRecoilValue(currentAnnotation)
  const [isAnnotationReady, setIsAnnotationReady] = useState(false)

  // Loading states for panorama, logo, thumbnails and map
  const [isPanoramaLoaded, setIsPanoramaLoaded] = useState(false)
  const [isLogoLoaded, setIsLogoLoaded] = useState(true)
  const [isMapLoaded, setIsMapLoaded] = useState(false)
  const [isThumbnailLoaded, setIsThumbnailLoaded] = useState(false)
  const isAllLoaded =
    isPanoramaLoaded && isThumbnailLoaded && isLogoLoaded && isMapLoaded

  const [isInitialLoad, setIsInitialLoad] = useState(true)
  // UI visibility for floor change handling
  const [thumbnailsReadyForFloor, setThumbnailsReadyForFloor] = useState<
    number | null
  >(null)
  const [uiVisible, setUIVisible] = useState(true)
  const prevFloorRef = useRef<number | null | undefined>(
    currentViewer.selectedFloor
  )

  const isCommunity = directory === 'community'
  const apiPrefix = isCommunity ? '/v1/tour' : '/v1'

  const { data: building } = useSWR<Building>({
    url: isCommunity
      ? `${apiPrefix}/buildings/${prams?.id}`
      : `${apiPrefix}/buildings/by-type?id=${prams?.id}&building_type=${getBuiildingType()}`
  })
  const { data: buildingInfo } = useSWR<Building>({
    url: `${apiPrefix}/buildings/${prams?.id}`
  })
  // get points for detail
  const { data, isValidating } = useSWR<FloorMapElement>(
    currentViewer?.selectedFloor || (!floorId && areaId)
      ? {
          url: `${apiPrefix}/points`,
          params: {
            buildingId: prams?.id,
            ...(currentViewer?.selectedFloor
              ? { floorId: currentViewer.selectedFloor }
              : { areaId })
          }
        }
      : null
  )

  useEffect(() => {
    if (!currentSelected || !currentSelected.type) {
      if (!currentSelected) setIsAnnotationReady(false)
      return
    }
    setIsAnnotationReady(true)
  }, [currentSelected])
  // set default selected Point
  useEffect(() => {
    if (!isValidating && building) {
      if (floorId && areaId && pointId && !currentViewer.isClickToNextFloor) {
        setCurrentViewer((prev) => ({
          ...prev,
          selectedFloor: +floorId,
          selectedArea: areaId,
          selectedPoint: pointId,
          selectedImage: subimageId ? +subimageId : prev.selectedImage
        }))
        return
      }
      if (floorId && !pointId && !currentViewer.isClickToNextFloor) {
        setCurrentViewer((prev) => ({
          ...prev,
          selectedFloor: +floorId,
          ...(areaId ? { selectedArea: areaId } : {})
        }))
        return
      }
      setCurrentViewer((prev) => {
        // If we have an areaId but no floorId yet, don't force a default floor yet.
        // We'll let the points fetch (triggered by areaId) find the correct floor.
        if (areaId && !floorId) {
          return prev
        }

        if (prev.selectedFloor !== null && prev.selectedFloor !== undefined) {
          const isFloorCurrent = building.floors.findIndex(
            (item) => item?.id === prev.selectedFloor
          )
          if (isFloorCurrent !== -1) {
            return prev
          }
        }
        const storedFloor = localStorage.getItem('floor')
        if (storedFloor) localStorage.removeItem('floor')
        const defaultFloor = storedFloor ? +storedFloor : building.defaultFloor

        return {
          ...prev,
          selectedFloor: defaultFloor || building.floors[0]?.id
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaId, building, floorId, isValidating, pointId, setCurrentViewer])

  useEffect(() => {
    if (data) {
      // If we landed with an areaId but no floorId, discover floor from points
      if (
        !currentViewer.selectedFloor &&
        areaId &&
        data.points.length > 0 &&
        data.points.some((p) => p.areaId === +areaId)
      ) {
        const firstPointInArea = data.points.find((p) => p.areaId === +areaId)
        if (firstPointInArea) {
          setCurrentViewer((prev) => ({
            ...prev,
            selectedFloor: firstPointInArea.floorId,
            selectedArea: areaId
          }))
          return
        }
      }

      setPoints(data.points)

      // prevent select default selected point when navigate
      const hasPoint =
        currentViewer?.selectedPoint &&
        data.points.some((item) => item.id === currentViewer.selectedPoint)

      if (hasPoint) {
        // Point found in current floor – use it
        setSelectedPointID(currentViewer.selectedPoint || data.points[0].id)
        setCurrentViewer((prev) => ({
          ...prev,
          selectedArea: `${data.points.find(
            (item) => item?.id === currentViewer.selectedPoint
          )?.areaId}`
        }))
      } else {
        // Point NOT in current floor or no selected point -> pick default
        let initialPoint: Point | null = null
        if (areaId) {
          const pointsInArea = data.points.filter(
            (item) => item.areaId === +areaId && item.image360
          )
          if (pointsInArea.length) {
            initialPoint = pointsInArea.reduce((acc, curr) =>
              curr.order < acc.order ? curr : acc
            )
          }
        }

        // Prefer server-provided defaultPoint when available
        if (!initialPoint && data.defaultPoint) {
          initialPoint =
            data.points.find((p) => p.id === data.defaultPoint?.id) || null
        }

        // If no area point or default point, pick the first image360 by order
        if (!initialPoint) {
          const image360Points = data.points.filter((item) => item.image360)
          if (image360Points.length) {
            initialPoint = image360Points.reduce((acc, curr) =>
              curr.order < acc.order ? curr : acc
            )
          }
        }

        if (initialPoint) {
          const initialPointId = initialPoint.id
          const initialPointArea = initialPoint.areaId
          setSelectedPointID(initialPointId)
          setCurrentViewer((prev) => ({
            ...prev,
            selectedArea: `${initialPointArea}`,
            selectedPoint: initialPointId,
            pointDetail: undefined // clear old point detail to avoid race
          }))
        } else {
          // Fallback to first point if no point found
          const fallbackPointId = data.points[0]?.id
          setSelectedPointID(fallbackPointId)
          setCurrentViewer((prev) => ({
            ...prev,
            selectedPoint: fallbackPointId,
            pointDetail: undefined
          }))
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, currentViewer.selectedFloor, areaId])

  // reset thumbnails loaded when points change
  useEffect(() => {
    if (points.length > 0) setIsThumbnailLoaded(false)
    else setIsThumbnailLoaded(true)
  }, [points.length])

  // Reset loading/UI states when floor changes.
  // NOTE: we do NOT reset selectedPoint here — that is handled by:
  //   • building effect  (URL params: floor + point)
  //   • handleChangeFloor (stairway: supplies new point directly)
  //   • data effect       (floor-selector: point not found → picks default)
  // Removing selectedPoint/setPoints resets here prevents the double-API-call
  // race condition that occurred on initial URL-driven floor set.
  useEffect(() => {
    if (prevFloorRef.current !== currentViewer.selectedFloor) {
      setUIVisible(false)
      setIsPanoramaLoaded(false)
      setIsThumbnailLoaded(false)
      setThumbnailsReadyForFloor(null)
      setIsMapLoaded(false)
    }
    prevFloorRef.current = currentViewer.selectedFloor
  }, [currentViewer.selectedFloor])

  // reset panorama loaded when selected point changes
  const prevSelectedPointRef = useRef<string | undefined>(
    currentViewer.selectedPoint
  )
  useEffect(() => {
    // Only reset if the point actually changed (not just on initial render)
    if (
      !isValidating &&
      currentViewer.selectedPoint &&
      prevSelectedPointRef.current !== currentViewer.selectedPoint
    ) {
      setIsPanoramaLoaded(false)
    }
    prevSelectedPointRef.current = currentViewer.selectedPoint
  }, [isValidating, currentViewer.selectedPoint])

  // Turn on isPanoramaLoaded when currentViewer.imageIsLoading toggles from true -> false
  const prevImageLoadingRef = useRef<boolean>(
    currentViewer.imageIsLoading || false
  )
  useEffect(() => {
    if (prevImageLoadingRef.current && !currentViewer.imageIsLoading) {
      setIsPanoramaLoaded(true)
    }
    prevImageLoadingRef.current = currentViewer.imageIsLoading || false
  }, [currentViewer.imageIsLoading])

  // FIX: Set isPanoramaLoaded = true when point has no image360
  // Use a small delay to ensure this runs after the reset useEffect
  useEffect(() => {
    if (currentViewer.pointDetail && !currentViewer.pointDetail.image360) {
      // Small timeout to ensure this runs after any reset
      const timer = setTimeout(() => {
        setIsPanoramaLoaded(true)
      }, 50)
      return () => clearTimeout(timer)
    }
  }, [currentViewer.pointDetail])

  // FIX: Set isPanoramaLoaded = true when there are no points to load
  useEffect(() => {
    const noDataPoints = data && (!data.points || data.points.length === 0)
    // Check local points state as well to ensure UI sync
    const noLocalPoints = !isValidating && points.length === 0 && data

    if (!isValidating && (noDataPoints || noLocalPoints)) {
      setIsPanoramaLoaded(true)
      setIsThumbnailLoaded(true)
    }
  }, [isValidating, data, points.length])

  // reset logo loaded state when building logo changes
  useEffect(() => {
    if (buildingInfo?.extraInfo?.logoUrl) {
      setIsLogoLoaded(false)
    } else {
      setIsLogoLoaded(true)
    }
  }, [buildingInfo?.extraInfo?.logoUrl])

  // Listen for manually set points from handleRedirect
  useEffect(() => {
    const handleSelectedPointIDUpdate = (pointId: string) => {
      setSelectedPointID(pointId)
    }

    ;(window as any).onSelectedPointIDUpdate = handleSelectedPointIDUpdate

    return () => {
      delete (window as any).onPointManuallySet
      delete (window as any).onSelectedPointIDUpdate
    }
  }, [])

  // Sync selectedPointID with currentViewer.selectedPoint
  useEffect(() => {
    if (
      currentViewer.selectedPoint &&
      currentViewer.selectedPoint !== selectedPointID
    ) {
      setSelectedPointID(currentViewer.selectedPoint)
    }
  }, [currentViewer.selectedPoint, selectedPointID])

  // When initial load is complete (both panorama & thumbnails loaded), end the initial load
  useEffect(() => {
    if (isInitialLoad && isAllLoaded) {
      setIsInitialLoad(false)
    }
  }, [isInitialLoad, isAllLoaded])

  const handleThumbnailsLoadedForFloor = (floorId?: string | undefined) => {
    setIsThumbnailLoaded(true)
    if (floorId !== undefined && floorId !== null) {
      const floorIdNum =
        typeof floorId === 'string' ? parseInt(floorId, 10) : floorId
      setThumbnailsReadyForFloor(floorIdNum)
    }
  }

  const handleMapLoaded = () => {
    setIsMapLoaded(true)
  }

  const shouldHideUI = !uiVisible || (isInitialLoad && !isAllLoaded)

  // Show UI when all necessary resources for the current floor are ready
  useEffect(() => {
    if (!uiVisible) {
      if (isInitialLoad && isAllLoaded) {
        setUIVisible(true)
        return
      }
      // FIX: Simplified - thumbnail is ready if loaded OR if no points exist
      const isThumbnailReady =
        thumbnailsReadyForFloor === currentViewer.selectedFloor ||
        isThumbnailLoaded

      if (
        !isInitialLoad &&
        isPanoramaLoaded &&
        isThumbnailLoaded &&
        isLogoLoaded &&
        isMapLoaded &&
        isThumbnailReady
      ) {
        setUIVisible(true)
      }
    }
  }, [
    uiVisible,
    isInitialLoad,
    isAllLoaded,
    thumbnailsReadyForFloor,
    currentViewer.selectedFloor,
    isPanoramaLoaded,
    isThumbnailLoaded,
    isLogoLoaded,
    isMapLoaded,
    points.length
  ])

  return (
    <Spinner
      spinning={isValidating || !uiVisible || (isInitialLoad && !isAllLoaded)}
    >
      <div style={{}}>
        <TourViewerFloorMap
          setSelectedPointID={setSelectedPointID}
          floors={building?.floors}
          onMapLoaded={handleMapLoaded}
          shouldHideUI={shouldHideUI}
          points={data?.points || []}
        />
      </div>

      {buildingInfo?.extraInfo?.logoUrl && (
        <div
          onClick={() => {
            window.open(buildingInfo?.extraInfo.logoLink, '_blank')
          }}
          className="absolute top-0 left-0 z-[98] cursor-pointer"
          style={{
            opacity: shouldHideUI ? 0 : 1,
            transition: 'opacity 0.25s ease-in-out'
          }}
        >
          <img
            className="max-h-[14vh] max-w-[17vw] w-auto object-contain"
            src={buildingInfo.extraInfo.logoUrl}
            alt="logo"
            loading="lazy"
            onLoad={() => setIsLogoLoaded(true)}
            onError={() => setIsLogoLoaded(true)}
          />
        </div>
      )}
      <div className="h-screen flex flex-col ">
        {/* Keep the panorama mounted but hide it with the same opacity transition as the UI
            so it becomes visible at the same time as other UI elements. */}
        <div
          style={{
            opacity: shouldHideUI ? 0 : 1,
            transition: 'opacity 0.25s ease-in-out',
            width: '100%',
            height: '100%'
          }}
        >
          {currentViewer.selectedPoint ? <TourViewerPanorama /> : null}
        </div>

        <div
          className="fixed bottom-0 left-0 right-0 z-10"
          style={{
            opacity: shouldHideUI ? 0 : 1,
            transition: 'opacity 0.25s ease-in-out'
          }}
        >
          <ThumbnailCarousel
            floors={building?.floors}
            building={building}
            setSelectedPointID={setSelectedPointID}
            setOpenBusinessInfo={setOpenBusinessInfo}
            setOpenMapLocation={setOpenMapLocation}
            onThumbnailsLoaded={handleThumbnailsLoadedForFloor}
          />
        </div>
      </div>
      {openBusinessInfo && <BusinessInfoModal setOpen={setOpenBusinessInfo} />}
      {openMapLocation && <MapLocationModal setOpen={setOpenMapLocation} />}
      {isAnnotationReady && <AnnotationModal />}
    </Spinner>
  )
}
